import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {Dropdown} from "primereact/dropdown";
import {OverlayPanel} from "primereact/overlaypanel";
import {ProgressSpinner} from "primereact/progressspinner";
import {Calendar} from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import api from "../../../system/api";
import Icon from "../../../system/components/icon";
import Avatar from "../../../system/components/avatar";
import {atHour, dueLabel, snoozeChoices, toPersianDigits} from "./my-work-utils";
import {useTask} from "../../../models/task/use-task";
import {useTaskDelay} from "../../../models/task-delay/use-task-delay";
import {upsertWorkspaceTask} from "../../../system/workspace-graph-slice";
import {useTranslate} from "../../../system/useTranslate";
import TaskSidebar from "../../projects/components/task-sidebar";

// Activity tab (Zofino master spec, section 3): "what exactly should I do now?" — the open tasks assigned to me,
// today first, with a Goal > Project > Milestone line, the referrer, the due time, a done checkbox and smart snooze.

const DATE_FILTERS = [
    {label: "امروز", value: "today"},
    {label: "فردا", value: "tomorrow"},
    {label: "هفته جاری", value: "week"},
    {label: "تاخیر خورده‌ها", value: "overdue"},
];
const EMPTY_FILTERS = {assignee: null, tag: null, referrer: null, project: null, team: null, date: null};

function PathLine({path}) {
    const parts = [path?.goal?.title, path?.project?.title, path?.milestone?.title].filter(Boolean);
    if (!parts.length) return <div className="my-work-path">بدون هدف و پروژه</div>;
    return (
        <div className="my-work-path" title={parts.join(" › ")}>
            {parts.map((part, index) => (
                <React.Fragment key={`${part}-${index}`}>
                    {index > 0 && <Icon icon="mi mi:chevron_left" className="text-xs"/>}
                    <span>{part}</span>
                </React.Fragment>
            ))}
        </div>
    );
}

function MyWorkTab() {
    const dispatch = useDispatch();
    const translate = useTranslate();
    const taskApi = useTask();
    const taskDelay = useTaskDelay();
    const workspaceId = useSelector((state) => state.app.workspaceMembershipData?.workspace?.documentId);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [options, setOptions] = useState({members: [], tags: [], projects: [], teams: []});
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyTask, setBusyTask] = useState(null);
    const [snoozeTask, setSnoozeTask] = useState(null);
    const [customDateOpen, setCustomDateOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(undefined); // TaskSidebar is closed when undefined
    const snoozePanel = useRef(null);

    const request = useCallback((url, params) => api.get(url, {
        params,
        headers: {workspace: workspaceId},
        suppressGlobalErrorHandler: true,
    }), [workspaceId]);

    useEffect(() => {
        if (!workspaceId) return;
        request("/my-work/options")
            .then(({data}) => setOptions(data?.data || data || {}))
            .catch(() => setOptions({members: [], tags: [], projects: [], teams: []}));
    }, [request, workspaceId]);

    const load = useCallback(async () => {
        if (!workspaceId) return;
        setLoading(true);
        setError(null);
        try {
            const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
            const {data} = await request("/my-work", params);
            setItems(data?.data || []);
        } catch (requestError) {
            setError(translate(requestError?.normalizedError?.message || requestError?.message || "errors.unexpected"));
        } finally {
            setLoading(false);
        }
    }, [filters, request, translate, workspaceId]);

    useEffect(() => {
        load();
    }, [load]);

    const setFilter = (key, value) => setFilters((current) => ({...current, [key]: value ?? null}));
    const hasFilters = Object.values(filters).some(Boolean);
    const viewingOthers = Boolean(filters.assignee);

    const memberOptions = useMemo(() => (options.members || []).map((member) => ({label: member.name, value: member.documentId, member})), [options.members]);
    const tagOptions = useMemo(() => (options.tags || []).map((tag) => ({label: tag, value: tag})), [options.tags]);
    const projectOptions = useMemo(() => (options.projects || []).map((item) => ({label: item.title, value: item.documentId})), [options.projects]);
    const teamOptions = useMemo(() => (options.teams || []).map((item) => ({label: item.name, value: item.documentId})), [options.teams]);

    const markDone = async (item) => {
        setBusyTask(item.documentId);
        try {
            const updated = await taskApi.updateTask(item.documentId, {taskStatus: "done"});
            if (!updated) return;
            dispatch(upsertWorkspaceTask(updated));
            setItems((current) => current.filter((task) => task.documentId !== item.documentId));
        } catch {
            // the global error toast already explains the failure
        } finally {
            setBusyTask(null);
        }
    };

    const snooze = async (newDueAt) => {
        const item = snoozeTask;
        if (!item || !newDueAt) return;
        snoozePanel.current?.hide();
        setBusyTask(item.documentId);
        try {
            await taskDelay.createTaskDelay(item.documentId, {newDueAt: new Date(newDueAt).toISOString()});
            await load();
        } catch {
            // the global error toast already explains the failure (for example a time before the current due date)
        } finally {
            setBusyTask(null);
            setSnoozeTask(null);
        }
    };

    const openTask = async (item) => {
        const task = await taskApi.getTask(item.documentId);
        if (task) {
            dispatch(upsertWorkspaceTask(task));
            setSelectedTask(task);
        }
    };

    const updateSelectedTask = async (taskData, updateData) => {
        if (!updateData) return null;
        const updated = await taskApi.updateTask(taskData.documentId, updateData);
        if (updated) {
            dispatch(upsertWorkspaceTask(updated));
            setSelectedTask(updated);
            load();
        }
        return updated;
    };

    const choices = snoozeTask ? snoozeChoices(snoozeTask) : null;

    return (
        <div className="h-full min-h-0 flex flex-column overflow-hidden" dir="rtl">
            <div className="my-work-filters">
                <Dropdown
                    value={filters.assignee}
                    options={memberOptions}
                    onChange={(event) => setFilter("assignee", event.value)}
                    placeholder="کارهای دیگران"
                    filter
                    showClear
                    className="my-work-filter"
                    emptyFilterMessage="عضوی پیدا نشد"
                />
                <Dropdown value={filters.tag} options={tagOptions} onChange={(event) => setFilter("tag", event.value)} placeholder="برچسب‌ها" filter showClear className="my-work-filter" emptyMessage="هنوز برچسبی ثبت نشده"/>
                <Dropdown value={filters.referrer} options={memberOptions} onChange={(event) => setFilter("referrer", event.value)} placeholder="ارجاع‌دهنده" filter showClear className="my-work-filter"/>
                <Dropdown value={filters.project} options={projectOptions} onChange={(event) => setFilter("project", event.value)} placeholder="پروژه" filter showClear className="my-work-filter"/>
                <Dropdown value={filters.team} options={teamOptions} onChange={(event) => setFilter("team", event.value)} placeholder="تیم" showClear className="my-work-filter" emptyMessage="هنوز تیمی ساخته نشده"/>
                <Dropdown value={filters.date} options={DATE_FILTERS} onChange={(event) => setFilter("date", event.value)} placeholder="تاریخ" showClear className="my-work-filter"/>
                <Button
                    type="button"
                    text
                    className="text-xs p-2 flex-shrink-0"
                    icon={<Icon icon="mi mi:filter_alt_off" className="text-lg"/>}
                    tooltip="پاک کردن فیلترها"
                    tooltipOptions={{position: "bottom"}}
                    aria-label="پاک کردن فیلترها"
                    disabled={!hasFilters}
                    onClick={() => setFilters(EMPTY_FILTERS)}
                />
            </div>

            <div className="flex-1 min-h-0 overflow-auto px-3 pb-3">
                <div className="text-xs text-500 py-2">
                    {viewingOthers
                        ? `کارهای محول‌شده به ${memberOptions.find((option) => option.value === filters.assignee)?.label || "این عضو"}`
                        : "من الان دقیقاً چه کاری باید انجام دهم؟ کارهای امروز اول آمده‌اند."}
                </div>
                {error && <div className="text-xs text-red-500 p-2">{error}</div>}
                {loading ? (
                    <div className="flex justify-content-center p-4"><ProgressSpinner style={{width: "2rem", height: "2rem"}} strokeWidth="4"/></div>
                ) : !items.length ? (
                    <div className="my-work-empty">
                        <Icon icon="mi mi:task_alt" className="text-4xl text-green-500"/>
                        <span>{hasFilters ? "کاری با این فیلترها پیدا نشد." : "کار بازی برای شما نمانده است."}</span>
                    </div>
                ) : (
                    <div className="flex flex-column gap-2">
                        {items.map((item) => (
                            <article
                                key={item.documentId}
                                className={`my-work-card ${item.isToday ? "is-today" : ""} ${item.isOverdue ? "is-overdue" : ""}`}
                                onClick={() => openTask(item)}
                            >
                                <div onClick={(event) => event.stopPropagation()} className="flex align-items-center">
                                    <Checkbox
                                        checked={false}
                                        disabled={busyTask === item.documentId}
                                        onChange={() => markDone(item)}
                                        aria-label="اتمام کار"
                                        tooltip="اتمام کار"
                                    />
                                </div>
                                <div className="flex-1 min-w-0 flex flex-column gap-1">
                                    <PathLine path={item.path}/>
                                    <div className="my-work-title">{item.title}</div>
                                    <div className="my-work-meta">
                                        {item.referrer?.name && (
                                            <span className="flex align-items-center gap-1">
                                                <Avatar memberData={item.referrer.member} size="small"/>
                                                ارسال‌شده توسط {item.referrer.name}
                                            </span>
                                        )}
                                        {item.referrer?.name && <span>•</span>}
                                        <span className={`flex align-items-center gap-1 ${item.isOverdue ? "text-red-500" : ""}`}>
                                            <Icon icon="mi mi:calendar_today" className="text-sm"/>
                                            سررسید: {dueLabel(item.dueDate)}
                                        </span>
                                        {(item.tags || []).map((tag) => <span key={tag} className="my-work-tag">#{tag}</span>)}
                                        {item.delayCount > 0 && <span className="text-orange-500">{toPersianDigits(item.delayCount)} بار تعویق</span>}
                                    </div>
                                </div>
                                <Button
                                    type="button"
                                    outlined
                                    severity="warning"
                                    className="text-xs py-2 px-3 flex-shrink-0"
                                    label="به تاخیر افتاد"
                                    icon={<Icon icon="mi mi:snooze" className="text-base ml-1"/>}
                                    loading={busyTask === item.documentId}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        setSnoozeTask(item);
                                        snoozePanel.current?.toggle(event);
                                    }}
                                />
                            </article>
                        ))}
                    </div>
                )}
            </div>

            <OverlayPanel ref={snoozePanel} onHide={() => { setSnoozeTask(null); setCustomDateOpen(false); }} className="my-work-snooze" dir="rtl">
                {choices && (
                    <div className="flex flex-column gap-3">
                        <div className="text-xs font-bold">تعویق تا امروز ساعت</div>
                        <div className="my-work-snooze-grid">
                            {choices.today.map((slot) => (
                                <Button
                                    key={slot.hour}
                                    type="button"
                                    outlined
                                    className="text-xs p-2 justify-content-center"
                                    label={toPersianDigits(`${slot.hour}:00`)}
                                    disabled={slot.disabled}
                                    onClick={() => snooze(slot.value)}
                                />
                            ))}
                        </div>
                        <div className="my-work-snooze-grid">
                            <Button type="button" outlined className="text-xs p-2 justify-content-center" label="فردا" disabled={choices.tomorrowDisabled} onClick={() => snooze(choices.tomorrow)}/>
                            <Button type="button" outlined className="text-xs p-2 justify-content-center" label="هفته بعد" tooltip="ابتدای هفته بعد، ساعت ۹" disabled={choices.nextWeekDisabled} onClick={() => snooze(choices.nextWeek)}/>
                            <Button
                                type="button"
                                outlined={!customDateOpen}
                                className="text-xs p-2 justify-content-center"
                                label="تاریخ دلخواه"
                                onClick={() => setCustomDateOpen((open) => !open)}
                            />
                        </div>
                        {customDateOpen && (
                            <Calendar
                                calendar={persian}
                                locale={persian_fa}
                                minDate={choices.firstCustomDay}
                                className="my-work-calendar"
                                onChange={(value) => {
                                    if (!value) return;
                                    const date = value.toDate();
                                    const due = snoozeTask?.dueDate ? new Date(snoozeTask.dueDate) : null;
                                    snooze(due ? atHour(date, due.getHours(), due.getMinutes()) : atHour(date, 9));
                                }}
                            />
                        )}
                    </div>
                )}
            </OverlayPanel>

            <TaskSidebar selectedTask={selectedTask} setSelectedTask={setSelectedTask} updateTask={updateSelectedTask}/>
        </div>
    );
}

export default MyWorkTab;
